/**
 * ImpersonationBanner — T21.
 *
 * Rendered above the header when the current session is an impersonation.
 * Shows a red full-width banner with the impersonated user's name and a
 * "Stop impersonating" button.
 *
 * On click:
 *   1. Calls POST /api/users/:id/impersonate/stop
 *   2. Calls authStore.stopImpersonating() to restore admin's own session
 *   3. If prevAccessToken was saved (T27), the admin is silently restored;
 *      otherwise the page reloads and the admin must log in again.
 */
import { Button, Group, Text } from '@mantine/core';
import { useState } from 'react';
import { usersApi } from '../api/endpoints/users';
import { useAuthStore } from '../state/auth-store';

export function ImpersonationBanner() {
  const user = useAuthStore((s) => s.user);
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  const stopImpersonating = useAuthStore((s) => s.stopImpersonating);
  const [loading, setLoading] = useState(false);

  // Only render when actively impersonating
  if (!isImpersonating || !user) {
    return null;
  }

  async function handleStop() {
    if (!user) return;
    setLoading(true);
    try {
      await usersApi.stopImpersonation(user.id);
    } catch {
      // If the API call fails (e.g. token already expired), still clear local
      // state so the UI doesn't get stuck in the impersonation banner.
    } finally {
      await stopImpersonating();
    }
  }

  return (
    <Group
      justify="center"
      align="center"
      gap="sm"
      px="md"
      py="xs"
      bg="red"
      data-testid="impersonation-banner"
      style={{ color: 'white' }}
    >
      <Text size="sm" fw={500} c="white">
        You are viewing as {user.name}. Read-only.
      </Text>
      <Button
        size="xs"
        variant="white"
        color="red"
        loading={loading}
        onClick={() => void handleStop()}
        data-testid="stop-impersonating-btn"
      >
        Stop impersonating
      </Button>
    </Group>
  );
}
